/** Plain-language comparison of a live athlete profile against a private SIIS draft. */
export type ProfileContent = Record<string, unknown>;

export const DRAFT_FIELD_LABELS: Record<string, string> = {
  name: "Display name", first_name: "First name", last_name: "Last name", sport: "Sport", country: "Country",
  tagline: "Tagline", bio: "Bio", story: "Your story", why_i_play: "Why I play", causes_text: "Causes I care about",
  mantra_quote: "Mantra", jersey_number: "Jersey number", position_short: "Position (short)", position_long: "Position",
  height: "Height", hometown: "Hometown", high_school: "High school", college: "College", occupation: "Occupation",
  years_playing: "Years playing", throws_hand: "Throws", image_path: "Main photo", team_logo_path: "Team logo",
  gallery_paths: "Photo gallery", video_urls: "Highlight videos", stats: "Stats", qa: "Q&A",
  custom_links: "Links", booking_ctas: "Booking buttons", partnership_interests: "Partnership interests",
  instagram_url: "Instagram", tiktok_url: "TikTok", facebook_url: "Facebook", youtube_url: "YouTube",
  linkedin_url: "LinkedIn", support_blurb: "Support message",
};

export type FieldChange = { key: string; label: string; kind: "added" | "removed" | "changed"; before: string; after: string };

const isEmpty = (v: unknown) =>
  v === null || v === undefined || (typeof v === "string" && v.trim() === "") || (Array.isArray(v) && v.length === 0);

/** Human-readable value: text as-is, lists as counts, photos/videos as counts. */
export const describeValue = (key: string, v: unknown): string => {
  if (isEmpty(v)) return "";
  if (Array.isArray(v)) {
    const n = v.length;
    if (key === "gallery_paths") return `${n} photo${n === 1 ? "" : "s"}`;
    if (key === "video_urls") return `${n} video${n === 1 ? "" : "s"}`;
    if (key === "qa") return `${n} question${n === 1 ? "" : "s"}`;
    return `${n} item${n === 1 ? "" : "s"}`;
  }
  if (key === "image_path" || key === "team_logo_path") return "Photo set";
  return typeof v === "object" ? JSON.stringify(v) : String(v);
};

const same = (a: unknown, b: unknown) => (isEmpty(a) && isEmpty(b)) || JSON.stringify(a) === JSON.stringify(b);

export const diffProfile = (live: ProfileContent, proposed: ProfileContent): FieldChange[] =>
  Object.keys(DRAFT_FIELD_LABELS)
    .filter((k) => !same(live[k], proposed[k]))
    .map((k) => {
      const before = describeValue(k, live[k]);
      const after = describeValue(k, proposed[k]);
      const photoSwap = (k === "image_path" || k === "team_logo_path") && before && after;
      return {
        key: k, label: DRAFT_FIELD_LABELS[k],
        kind: !before ? "added" : !after ? "removed" : "changed",
        before, after: photoSwap ? "New photo" : after,
      };
    });
